import { Api } from '../services/api.ts';
import type { MatrizMorfologica, AtlasTactica, AtlasCaso } from '../types/index.ts';

let matrizCache: MatrizMorfologica[] = [];
let tacticasCache: AtlasTactica[] = [];
let casosCache: AtlasCaso[] = [];

export function initCasos() {
  // Elementos Matriz Morfológica (OE1)
  const tableMatrizBody = document.querySelector('#table-matriz-beauty tbody') as HTMLTableSectionElement | null;
  const formMatrizTitle = document.getElementById('m-form-title');
  const btnSaveMatriz = document.getElementById('btn-save-matriz') as HTMLButtonElement | null;
  const btnClearMatriz = document.getElementById('btn-clear-matriz') as HTMLButtonElement | null;
  const btnPrintMatriz = document.getElementById('btn-print-matriz') as HTMLButtonElement | null;

  // Elementos Resistencia Táctica (OE3)
  const tableTacticasBody = document.querySelector('#table-tacticas tbody') as HTMLTableSectionElement | null;
  const formTacticaTitle = document.getElementById('tac-form-title');
  const btnSaveTactica = document.getElementById('btn-save-tactica') as HTMLButtonElement | null;
  const btnClearTactica = document.getElementById('btn-clear-tactica') as HTMLButtonElement | null;

  async function loadData() {
    try {
      const [casos, morfo, tacticas] = await Promise.all([
        Api.getCasos(),
        Api.getMatrizMorfologica(),
        Api.getTacticas()
      ]);

      casosCache = casos;
      matrizCache = morfo;
      tacticasCache = tacticas;

      populateCasoSelects();
      renderMatriz();
      renderTacticas();
    } catch (err) {
      console.error('Error al cargar datos de casos y matriz:', err);
    }
  }

  function populateCasoSelects() {
    const selCaso = document.getElementById('m-caso') as HTMLSelectElement | null;
    const selTacCaso = document.getElementById('tac-caso') as HTMLSelectElement | null;

    [selCaso, selTacCaso].forEach(select => {
      if (!select) return;
      const currentVal = select.value;
      select.innerHTML = '';
      casosCache.forEach(c => {
        const opt = document.createElement('option');
        opt.value = c.id.toString();
        opt.textContent = `${c.name} (${c.codigo})`;
        select.appendChild(opt);
      });
      if (currentVal) select.value = currentVal;
    });
  }

  // =========================================================================
  // 1. MATRIZ MORFOLÓGICA (OE1)
  // =========================================================================
  function renderMatriz() {
    if (!tableMatrizBody) return;
    tableMatrizBody.innerHTML = '';

    if (matrizCache.length === 0) {
      tableMatrizBody.innerHTML = `<tr><td colspan="5" style="text-align:center; padding:20px; color:var(--text-muted); font-style:italic;">No hay registros morfológicos. Registra uno a continuación.</td></tr>`;
      return;
    }

    matrizCache.forEach(m => {
      const tr = document.createElement('tr');
      const casoNombre = m.caso_nombre || casosCache.find(c => c.id === m.caso_id)?.name || 'Territorio';
      const escalaText = m.escala_transecto === 'macro' ? 'Macrotransecto' : (m.escala_transecto === 'meso' ? 'Mesotransecto' : 'Microtransecto');

      tr.innerHTML = `
        <td>
          <div style="font-weight:700; font-size:0.92rem; color:var(--text-main);">${escapeHtml(casoNombre)}</div>
          <span class="badge" style="background:var(--primary-subtle); color:var(--strawbs); font-family:var(--font-mono); margin-top:4px;">Año: ${escapeHtml(m.anio_plan || 'Por verificar')}</span>
          <div style="margin-top:6px; font-size:0.8rem;"><strong>Plan:</strong> ${escapeHtml(m.name || 'Sin identificar')}</div>
          <div style="font-size:0.75rem; color:var(--text-muted); margin-top:4px;"><strong>Responsable:</strong> ${escapeHtml(m.responsable || 'Por investigar')}</div>
          <div style="font-size:0.75rem; color:var(--text-muted);"><strong>Estado:</strong> ${escapeHtml(m.estado || 'Por verificar')}</div>
          <div style="display:flex; gap:6px; margin-top:10px;">
            <button class="btn-secondary btn-edit-morfo" data-id="${m.id}" style="padding:4px 9px; font-size:0.75rem;">Editar</button>
            <button class="btn-del btn-del-morfo" data-id="${m.id}" style="padding:4px 8px;" title="Eliminar de SQLite">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
            </button>
          </div>
        </td>
        <td style="font-size:0.82rem; line-height:1.5;">
          <div style="font-size:0.7rem; font-weight:700; text-transform:uppercase; color:var(--strawbs); margin-bottom:4px;">Trazado & Estratigrafía (Panerai)</div>
          ${escapeHtml(m.morfo_original || 'Pendiente de registro')}
        </td>
        <td style="font-size:0.82rem; line-height:1.5;">
          <div style="font-size:0.7rem; font-weight:700; text-transform:uppercase; color:var(--text-muted); margin-bottom:4px;">Grano Urbano & Vías (Prinz)</div>
          <div><strong>Pre:</strong> ${escapeHtml(m.grano_pre || 'Sin datos')}</div>
          <div style="color:var(--strawbs);"><strong>Post:</strong> ${escapeHtml(m.grano_post || 'Sin datos')}</div>
          <div style="margin-top:6px; font-size:0.78rem;"><strong>Calles / H/D:</strong> ${escapeHtml(m.tipologia_calles || 'Pendiente')}</div>
        </td>
        <td style="font-size:0.82rem; line-height:1.5;">
          <div style="font-size:0.7rem; font-weight:700; text-transform:uppercase; color:var(--woven-basket); margin-bottom:4px;">Impacto Sociofísico (Secchi)</div>
          <div><strong>Equipamientos:</strong> ${escapeHtml(m.equipamientos || 'Pendiente')}</div>
          <div style="color:#b91c1c; margin-top:3px;"><strong>Conflictos:</strong> ${escapeHtml(m.conflictos || 'Pendiente')}</div>
          <div style="margin-top:4px; font-size:0.78rem;"><strong>Población / Desplazamiento:</strong> ${escapeHtml(m.poblacion_obs || 'Pendiente')}</div>
        </td>
        <td style="font-size:0.82rem; line-height:1.5;">
          <div style="font-size:0.7rem; font-weight:700; text-transform:uppercase; color:var(--leaf-hat); margin-bottom:4px;">Ejecución & Fuentes</div>
          <div><strong>Escala:</strong> ${escalaText}</div>
          <div><strong>Periodo:</strong> ${escapeHtml(m.ejecucion || 'Por verificar')}</div>
          <div style="margin-top:4px; font-size:0.78rem;"><strong>Actuaciones:</strong> ${escapeHtml(m.actuaciones || 'Pendiente')}</div>
          <div style="margin-top:4px; font-size:0.78rem;"><strong>Fuentes:</strong> ${escapeHtml(m.fuentes || 'Pendiente')}</div>
          ${m.url_fuente ? `<div style="margin-top:4px;"><a href="${escapeHtml(m.url_fuente)}" target="_blank" rel="noopener" style="color:var(--strawbs); text-decoration:underline; font-size:0.76rem;">Abrir fuente ↗</a></div>` : ''}
        </td>
      `;

      tableMatrizBody.appendChild(tr);
    });

    // Eventos editar y eliminar Morfología
    tableMatrizBody.querySelectorAll('.btn-edit-morfo').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-id');
        const m = matrizCache.find(x => x.id.toString() === id);
        if (!m) return;
        editMatriz(m);
      });
    });

    tableMatrizBody.querySelectorAll('.btn-del-morfo').forEach(btn => {
      btn.addEventListener('click', async () => {
        const id = btn.getAttribute('data-id');
        if (!id) return;
        if (confirm('¿Eliminar esta fila de la Matriz Morfológica en SQLite?')) {
          try {
            await Api.deleteMatrizMorfologica(id);
            matrizCache = matrizCache.filter(x => x.id.toString() !== id);
            renderMatriz();
          } catch (e) {
            alert('Error al eliminar fila');
          }
        }
      });
    });
  }

  function editMatriz(m: MatrizMorfologica) {
    (document.getElementById('m-id-edit') as HTMLInputElement).value = m.id.toString();
    (document.getElementById('m-caso') as HTMLSelectElement).value = m.caso_id.toString();
    (document.getElementById('m-plan') as HTMLInputElement).value = m.name || '';
    (document.getElementById('m-anio') as HTMLInputElement).value = m.anio_plan || '';
    (document.getElementById('m-morfo') as HTMLTextAreaElement).value = m.morfo_original || '';
    (document.getElementById('m-grano-pre') as HTMLInputElement).value = m.grano_pre || '';
    (document.getElementById('m-grano-post') as HTMLInputElement).value = m.grano_post || '';
    (document.getElementById('m-calles') as HTMLTextAreaElement).value = m.tipologia_calles || '';
    (document.getElementById('m-equipos') as HTMLInputElement).value = m.equipamientos || '';
    (document.getElementById('m-conflictos') as HTMLInputElement).value = m.conflictos || '';
    (document.getElementById('m-pob') as HTMLTextAreaElement).value = m.poblacion_obs || '';
    (document.getElementById('m-escala') as HTMLSelectElement).value = m.escala_transecto || 'macro';
    (document.getElementById('m-responsable') as HTMLInputElement).value = m.responsable || '';
    (document.getElementById('m-ejecucion') as HTMLInputElement).value = m.ejecucion || '';
    (document.getElementById('m-estado') as HTMLSelectElement).value = m.estado || 'verificar';
    (document.getElementById('m-actuaciones') as HTMLTextAreaElement).value = m.actuaciones || '';
    (document.getElementById('m-fuentes') as HTMLTextAreaElement).value = m.fuentes || '';
    (document.getElementById('m-url') as HTMLInputElement).value = m.url_fuente || '';

    if (formMatrizTitle) formMatrizTitle.innerText = `Editando Análisis Morfológico (${m.name})`;
    if (btnSaveMatriz) btnSaveMatriz.innerText = 'Actualizar Análisis en SQLite';
    if (btnClearMatriz) btnClearMatriz.style.display = 'inline-flex';

    formMatrizTitle?.scrollIntoView({ behavior: 'smooth' });
  }

  btnClearMatriz?.addEventListener('click', () => {
    (document.getElementById('m-id-edit') as HTMLInputElement).value = '';
    const formInputs = document.querySelectorAll('#mod-analisis input:not([type="hidden"]), #mod-analisis textarea');
    formInputs.forEach(el => (el as HTMLInputElement).value = '');
    if (formMatrizTitle) formMatrizTitle.innerText = 'Ingreso de Datos Morfotipológicos y Sociourbanos';
    if (btnSaveMatriz) btnSaveMatriz.innerText = 'Guardar Análisis en SQLite';
    if (btnClearMatriz) btnClearMatriz.style.display = 'none';
  });

  btnSaveMatriz?.addEventListener('click', async () => {
    const editId = (document.getElementById('m-id-edit') as HTMLInputElement).value;
    const plan = (document.getElementById('m-plan') as HTMLInputElement).value.trim();
    if (!plan) return alert('Por favor, indica el nombre del Plan Urbanístico o Eje.');

    const data: Partial<MatrizMorfologica> = {
      caso_id: parseInt((document.getElementById('m-caso') as HTMLSelectElement).value) || 1,
      name: plan,
      anio_plan: (document.getElementById('m-anio') as HTMLInputElement).value.trim(),
      escala_transecto: (document.getElementById('m-escala') as HTMLSelectElement).value as any,
      morfo_original: (document.getElementById('m-morfo') as HTMLTextAreaElement).value.trim(),
      grano_pre: (document.getElementById('m-grano-pre') as HTMLInputElement).value.trim(),
      grano_post: (document.getElementById('m-grano-post') as HTMLInputElement).value.trim(),
      tipologia_calles: (document.getElementById('m-calles') as HTMLTextAreaElement).value.trim(),
      equipamientos: (document.getElementById('m-equipos') as HTMLInputElement).value.trim(),
      conflictos: (document.getElementById('m-conflictos') as HTMLInputElement).value.trim(),
      poblacion_obs: (document.getElementById('m-pob') as HTMLTextAreaElement).value.trim(),
      responsable: (document.getElementById('m-responsable') as HTMLInputElement).value.trim(),
      ejecucion: (document.getElementById('m-ejecucion') as HTMLInputElement).value.trim(),
      estado: (document.getElementById('m-estado') as HTMLSelectElement).value as any,
      actuaciones: (document.getElementById('m-actuaciones') as HTMLTextAreaElement).value.trim(),
      fuentes: (document.getElementById('m-fuentes') as HTMLTextAreaElement).value.trim(),
      url_fuente: (document.getElementById('m-url') as HTMLInputElement).value.trim()
    };

    try {
      if (editId) {
        await Api.updateMatrizMorfologica(editId, data);
        const idx = matrizCache.findIndex(x => x.id.toString() === editId);
        if (idx !== -1) {
          matrizCache[idx] = { ...matrizCache[idx], ...data } as MatrizMorfologica;
        }
      } else {
        const res = await Api.addMatrizMorfologica(data);
        data.id = res.id;
        const casoRow = casosCache.find(c => c.id === data.caso_id);
        data.caso_nombre = casoRow?.name;
        matrizCache.push(data as MatrizMorfologica);
      }

      renderMatriz();
      btnClearMatriz?.click();
    } catch (err: any) {
      alert('Error al guardar análisis morfológico: ' + (err?.message || err));
    }
  });

  btnPrintMatriz?.addEventListener('click', () => {
    window.print();
  });

  // =========================================================================
  // 2. RESISTENCIA TÁCTICA (OE3)
  // =========================================================================
  function renderTacticas() {
    if (!tableTacticasBody) return;
    tableTacticasBody.innerHTML = '';

    if (tacticasCache.length === 0) {
      tableTacticasBody.innerHTML = `<tr><td colspan="4" style="text-align:center; padding:20px; color:var(--text-muted); font-style:italic;">No hay tácticas registradas aún. Registra una a continuación.</td></tr>`;
      return;
    }

    tacticasCache.forEach(t => {
      const tr = document.createElement('tr');
      const casoNombre = t.caso_nombre || casosCache.find(c => c.id === t.caso_id)?.name || 'Territorio';
      const badgeClass = t.tipo_tactica.includes('simb') ? 'badge-pending' : (t.tipo_tactica.includes('cuidado') ? 'badge-done' : 'badge-active');

      tr.innerHTML = `
        <td>
          <div style="font-weight:700; color:var(--text-main); font-size:0.9rem;">${escapeHtml(casoNombre)}</div>
          <span class="badge ${badgeClass}" style="margin-top:4px; font-size:0.72rem;">${escapeHtml(t.tipo_tactica.toUpperCase())}</span>
        </td>
        <td style="font-size:0.84rem; line-height:1.5;">
          <strong>${escapeHtml(t.name)}</strong>
          <p style="margin-top:4px; color:var(--text-main); font-size:0.82rem;">${escapeHtml(t.descripcion || '')}</p>
          ${t.fuente_registro ? `<div style="margin-top:4px; font-size:0.75rem; color:var(--text-muted);"><strong>Fuente:</strong> ${escapeHtml(t.fuente_registro)}</div>` : ''}
          ${t.agente_comunitario ? `<div style="font-size:0.75rem; color:var(--strawbs);"><strong>Agente:</strong> ${escapeHtml(t.agente_comunitario)}</div>` : ''}
        </td>
        <td style="font-size:0.8rem; color:var(--text-muted);">
          ${escapeHtml(t.glitch_nombre || 'Transversal')}
        </td>
        <td style="text-align:center;">
          <div style="display:flex; justify-content:center; gap:6px;">
            <button class="btn-secondary btn-edit-tactica" data-id="${t.id}" style="padding:4px 9px; font-size:0.75rem;">Editar</button>
            <button class="btn-del btn-del-tactica" data-id="${t.id}" style="padding:4px 8px;" title="Eliminar táctica">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
            </button>
          </div>
        </td>
      `;

      tableTacticasBody.appendChild(tr);
    });

    tableTacticasBody.querySelectorAll('.btn-edit-tactica').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-id');
        const t = tacticasCache.find(x => x.id.toString() === id);
        if (!t) return;
        editTactica(t);
      });
    });

    tableTacticasBody.querySelectorAll('.btn-del-tactica').forEach(btn => {
      btn.addEventListener('click', async () => {
        const id = btn.getAttribute('data-id');
        if (!id) return;
        if (confirm('¿Eliminar esta táctica de resistencia de SQLite?')) {
          try {
            await Api.deleteTactica(id);
            tacticasCache = tacticasCache.filter(x => x.id.toString() !== id);
            renderTacticas();
          } catch (e) {
            alert('Error al eliminar táctica');
          }
        }
      });
    });
  }

  function editTactica(t: AtlasTactica) {
    (document.getElementById('tac-id-edit') as HTMLInputElement).value = t.id.toString();
    (document.getElementById('tac-caso') as HTMLSelectElement).value = t.caso_id.toString();
    (document.getElementById('tac-tipo') as HTMLSelectElement).value = t.tipo_tactica || 'espacial';
    (document.getElementById('tac-desc') as HTMLTextAreaElement).value = t.descripcion || '';
    (document.getElementById('tac-glitch') as HTMLInputElement).value = t.glitch_nombre || '';
    (document.getElementById('tac-fuente') as HTMLInputElement).value = t.fuente_registro || '';
    (document.getElementById('tac-agente') as HTMLInputElement).value = t.agente_comunitario || '';

    if (formTacticaTitle) formTacticaTitle.innerText = `Editando Táctica (${t.name})`;
    if (btnSaveTactica) btnSaveTactica.innerText = 'Actualizar Táctica en SQLite';
    if (btnClearTactica) btnClearTactica.style.display = 'inline-flex';

    formTacticaTitle?.scrollIntoView({ behavior: 'smooth' });
  }

  btnClearTactica?.addEventListener('click', () => {
    (document.getElementById('tac-id-edit') as HTMLInputElement).value = '';
    const formInputs = document.querySelectorAll('#mod-tacticas input:not([type="hidden"]), #mod-tacticas textarea');
    formInputs.forEach(el => (el as HTMLInputElement).value = '');
    if (formTacticaTitle) formTacticaTitle.innerText = 'Catálogo de Repertorios de Resistencia Táctica (OE3)';
    if (btnSaveTactica) btnSaveTactica.innerText = 'Registrar Táctica en SQLite';
    if (btnClearTactica) btnClearTactica.style.display = 'none';
  });

  btnSaveTactica?.addEventListener('click', async () => {
    const editId = (document.getElementById('tac-id-edit') as HTMLInputElement).value;
    const desc = (document.getElementById('tac-desc') as HTMLTextAreaElement).value.trim();
    if (!desc) return alert('Por favor, ingresa la descripción de la táctica observada.');

    const name = desc.slice(0, 40) + (desc.length > 40 ? '...' : '');
    const data: Partial<AtlasTactica> = {
      caso_id: parseInt((document.getElementById('tac-caso') as HTMLSelectElement).value) || 1,
      name,
      tipo_tactica: (document.getElementById('tac-tipo') as HTMLSelectElement).value as any,
      descripcion: desc,
      fuente_registro: (document.getElementById('tac-fuente') as HTMLInputElement).value.trim(),
      agente_comunitario: (document.getElementById('tac-agente') as HTMLInputElement).value.trim()
    };

    try {
      if (editId) {
        await Api.updateTactica(editId, data);
        const idx = tacticasCache.findIndex(x => x.id.toString() === editId);
        if (idx !== -1) {
          tacticasCache[idx] = { ...tacticasCache[idx], ...data } as AtlasTactica;
        }
      } else {
        const res = await Api.addTactica(data);
        data.id = res.id;
        const casoRow = casosCache.find(c => c.id === data.caso_id);
        data.caso_nombre = casoRow?.name;
        tacticasCache.push(data as AtlasTactica);
      }

      renderTacticas();
      btnClearTactica?.click();
    } catch (err: any) {
      alert('Error al guardar táctica en SQLite: ' + (err?.message || err));
    }
  });

  function escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.innerText = str || '';
    return div.innerHTML;
  }

  loadData();
}
